<script setup lang="ts">
import { computed, ref } from 'vue';

import IconProfile from '@/components/icons/IconProfile.vue';
import { getInitials } from '@/utils/formatters';

interface Props {
  avatarUrl?: string;
  name?: string;
  small?: boolean;
  subtext?: string;
  disabled?: boolean;
  vertical?: boolean;
  search?: boolean;
  badge?: boolean;
  hidePlaceholder?: boolean;
  hideInitials?: boolean;
  big?: boolean;
  fullWidth?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  avatarUrl: '',
  name: '',
  subtext: ''
});

const showPlaceholder = ref(false);

const onImageError = () => {
  showPlaceholder.value = true;
};

const initials = computed(() => {
  return getInitials(props.name);
});
</script>

<template>
  <div
    class="avatar"
    :class="{ 'avatar--vertical': vertical }"
  >
    <div
      v-if="!hidePlaceholder"
      class="avatar__image-wrapper"
      :class="{
        'avatar__image-wrapper--disabled': disabled,
        'avatar__image-wrapper--small': small,
        'avatar__image-wrapper--search': search,
        'avatar__image-wrapper--badge': badge,
        'avatar__image-wrapper--big': big
      }"
    >
      <img
        v-if="avatarUrl && !showPlaceholder"
        :src="avatarUrl"
        alt="Avatar"
        class="avatar__image"
        loading="lazy"
        @error="onImageError"
      >
      <div
        v-else
        class="avatar__placeholder"
      >
        <span v-if="initials && !hideInitials">{{ initials }}</span>
        <IconProfile v-else />
      </div>
    </div>

    <div
      v-if="name"
      class="avatar__content"
    >
      <span
        class="avatar__name"
        :class="{
          'avatar__name--disabled': disabled,
          'avatar__name--search': search,
          'avatar__name--badge': badge,
          'avatar__name--full-width': fullWidth
        }"
      >
        {{ name }}
      </span>
      <span class="avatar__subtext">
        <span
          v-if="subtext"
          class="avatar__subtext--space ellipsis-text"
          :class="{
            'avatar__subtext--disabled': disabled,
            'avatar__subtext--search': search,
          }"
        >{{ subtext }}</span>
        <slot name="details" />
      </span>
    </div>
  </div>
</template>

<style lang="scss">
.avatar__placeholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--gray);
  border: 1px solid var(--gray-dark);
}
</style>

<style scoped lang="scss">
.avatar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.avatar--vertical {
  flex-direction: column;
  justify-content: center;

  .avatar__name {
    font-size: 12px;
    font-weight: normal;
    max-width: 72px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    margin-top: 8px;
  }

  .avatar__image-wrapper {
    margin-right: 0;
  }
}

.avatar__image-wrapper {
  margin-right: 10px;
  position: relative;
}

.avatar__image-wrapper--disabled {
  &::before {
    content: '';
    background-color: white;
    display: flex;
    height: 100%;
    width: 100%;
    position: absolute;
    opacity: 0.5;
  }
}

.avatar__image-wrapper--search {
  .avatar__image,
  .avatar__placeholder {
    min-width: 32px;
    width: 32px;
    height: 32px;
    font-size: 12px;
  }
}

.avatar__image-wrapper--small {
  .avatar__image,
  .avatar__placeholder {
    min-width: 32px;
    width: 32px;
    height: 32px;
  }
}

.avatar__image-wrapper--badge {
  .avatar__image,
  .avatar__placeholder {
    min-width: 16px;
    width: 16px;
    height: 16px;
    font-size: 8px;
  }
}

.avatar__image-wrapper--big {
  .avatar__image,
  .avatar__placeholder {
    min-width: 96px;
    width: 96px;
    height: 96px;
    font-size: 28px;

    svg {
      width: 34px;
      height: 34px;
    }
  }
}

.avatar__image {
  min-width: 40px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.avatar__content {
  display: flex;
  flex-direction: column;
}

.avatar__name {
  font-size: 16px;
  font-weight: 500;
  color: var(--text-color);
  max-width: 260px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.avatar__subtext {
  font-size: 14px;
  color: var(--gray-darkest);
  display: flex;
  gap: 8px;
}

.avatar__subtext--search {
  font-size: 12px;
  color: var(--text-color-gray);
  max-width: 230px;
}

.avatar__name--disabled,
.avatar__subtext--disabled {
  color: var(--gray-darkest);
}

.avatar__name--search {
  font-size: 14px;
  font-weight: 600;
  max-width: 230px;
}

.avatar__name--badge {
  font-size: 14px;
  font-weight: 500;
  text-wrap: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.avatar__name--full-width {
  max-width: 100%;
}
</style>
